import { CircleAlert } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'

import { Button } from '../components/ui/Button'
import { EmptyState } from '../components/ui/EmptyState'
import { useT } from '../i18n'
import styles from './ErrorBoundary.module.css'

interface Labels {
  title: string
  hint: string
  action: string
}

interface Props {
  labels: Labels
  children: ReactNode
}

interface State {
  failed: boolean
}

/**
 * The last line of defence.
 *
 * Without one, a single throw during render — a balance row that arrives as an
 * empty string and blows up `new Decimal`, a field the contract says is there
 * and is not — unmounts the entire tree and leaves the user on a blank white
 * page with no way back but a manual reload. Amounts are parsed defensively
 * (lib/decimal.ts) precisely so this never fires; it exists for the case where
 * something else does.
 */
class Boundary extends Component<Props, State> {
  override state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Not swallowed: the stack has to reach the console (and any error reporter
    // installed on top of it), or a crash that recovers becomes invisible.
    console.error('Unhandled render error', error, info.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children

    const { title, hint, action } = this.props.labels
    return (
      <div className={styles.crash} role="alert">
        <EmptyState
          icon={<CircleAlert size={28} strokeWidth={1.5} />}
          title={title}
          body={hint}
          action={
            <Button variant="primary" onClick={() => window.location.reload()}>
              {action}
            </Button>
          }
        />
      </div>
    )
  }
}

/** Reads the dictionary outside the class, which cannot use hooks. */
export function ErrorBoundary({ children }: { children: ReactNode }): JSX.Element {
  const t = useT()
  return (
    <Boundary
      labels={{
        title: t.common.somethingWentWrong,
        hint: t.common.crashHint,
        action: t.common.reload,
      }}
    >
      {children}
    </Boundary>
  )
}
